import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ToasterService } from '../modules/toaster/toaster.service';
import { ApiError, hasHandledUserVisiblePath, isApiError, markApiErrorHandled } from '../transport/api-error';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  private readonly defaultErrorMessage = 'Request failed. Please try again.';
  private readonly defaultNetworkErrorMessage = 'Network error. Please check your connection and try again.';

  private readonly toasterService: ToasterService = inject(ToasterService);

  handle(error: unknown): Observable<never> {
    const apiError = this.normalize(error);

    if (!hasHandledUserVisiblePath(apiError)) {
      this.toasterService.error(apiError.message);
      markApiErrorHandled(apiError);
    }

    return throwError(() => apiError);
  }

  normalize(error: unknown): ApiError {
    if (isApiError(error)) {
      return error;
    }

    const payload = this.extractPayload(error);
    const status = this.extractStatus(error, payload);

    if (status === 0) {
      return {
        status: 0,
        code: 'NETWORK_ERROR',
        message: this.defaultNetworkErrorMessage,
      };
    }

    const messageDescriptor = this.describePayload(payload);
    const message = this.resolveMessage(messageDescriptor, status);
    const code = this.resolveCode(payload, status);

    return {
      status,
      code,
      message,
      ...(Object.keys(messageDescriptor.details).length > 0 && { details: messageDescriptor.details }),
    };
  }

  private extractPayload(error: unknown): unknown {
    if (error instanceof HttpErrorResponse) {
      return error.error ?? error.message;
    }

    if (!this.isRecord(error)) {
      return error;
    }

    if (Object.prototype.hasOwnProperty.call(error, 'error')) {
      return error['error'];
    }

    return error;
  }

  private extractStatus(error: unknown, payload: unknown): number {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }

    if (this.isRecord(error)) {
      if (typeof error['status'] === 'number') {
        return error['status'];
      }

      if (typeof error['statusCode'] === 'number') {
        return error['statusCode'];
      }
    }

    if (this.isRecord(payload)) {
      if (typeof payload['status'] === 'number') {
        return payload['status'];
      }

      if (typeof payload['statusCode'] === 'number') {
        return payload['statusCode'];
      }
    }

    return 0;
  }

  private describePayload(payload: unknown): { rootMessages: string[]; details: Record<string, string[]> } {
    const explicitMessages = this.extractExplicitMessages(payload);
    if (explicitMessages.length > 0) {
      return {
        rootMessages: explicitMessages,
        details: {},
      };
    }

    const description = {
      rootMessages: [] as string[],
      details: {} as Record<string, string[]>,
    };

    this.collectPayloadMessages(payload, description);

    return description;
  }

  private extractExplicitMessages(payload: unknown): string[] {
    if (payload == null || payload === '') {
      return [];
    }

    if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean' || Array.isArray(payload)) {
      return this.flattenInlineMessage(payload);
    }

    if (!this.isRecord(payload) || payload['message'] === undefined) {
      return [];
    }

    return this.flattenInlineMessage(payload['message']);
  }

  private flattenInlineMessage(payload: unknown): string[] {
    if (payload == null || payload === '') {
      return [];
    }

    if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean') {
      const value = String(payload).trim();
      return value ? [value] : [];
    }

    if (Array.isArray(payload)) {
      return payload.flatMap((value) => this.flattenInlineMessage(value));
    }

    if (!this.isRecord(payload)) {
      return [];
    }

    return Object.entries(payload)
      .filter(([key]) => !this.isMetaKey(key))
      .flatMap(([key, value]) => this.flattenInlineMessage(value).map((message) => `${key}: ${message}`));
  }

  private collectPayloadMessages(
    payload: unknown,
    description: { rootMessages: string[]; details: Record<string, string[]> },
    fieldPath = ''
  ): void {
    if (payload == null || payload === '') {
      return;
    }

    if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean') {
      const value = String(payload).trim();
      if (!value) {
        return;
      }

      if (fieldPath) {
        description.details[fieldPath] ??= [];
        description.details[fieldPath].push(value);
        return;
      }

      description.rootMessages.push(value);
      return;
    }

    if (Array.isArray(payload)) {
      payload.forEach((value) => this.collectPayloadMessages(value, description, fieldPath));
      return;
    }

    if (!this.isRecord(payload)) {
      return;
    }

    Object.entries(payload)
      .filter(([key]) => !this.isMetaKey(key))
      .forEach(([key, value]) => {
        const nestedFieldPath = key === 'error' && fieldPath === ''
          ? ''
          : fieldPath ? `${fieldPath}.${key}` : key;

        this.collectPayloadMessages(value, description, nestedFieldPath);
      });
  }

  private resolveMessage(
    description: { rootMessages: string[]; details: Record<string, string[]> },
    status: number
  ): string {
    if (description.rootMessages.length > 0) {
      return description.rootMessages.join('; ');
    }

    const detailMessages = Object.entries(description.details)
      .flatMap(([field, values]) => values.map((value) => `${field}: ${value}`));

    if (detailMessages.length > 0) {
      return detailMessages.join('; ');
    }

    return status === 0
      ? this.defaultNetworkErrorMessage
      : this.defaultErrorMessage;
  }

  private resolveCode(payload: unknown, status: number): string {
    if (this.isRecord(payload)) {
      if (typeof payload['code'] === 'string' && payload['code'].trim()) {
        return this.normalizeCode(payload['code']);
      }

      const hasEnvelopeMarkers = payload['message'] !== undefined
        || typeof payload['status'] === 'number'
        || typeof payload['statusCode'] === 'number'
        || typeof payload['code'] === 'string';

      if (hasEnvelopeMarkers && typeof payload['error'] === 'string' && payload['error'].trim()) {
        return this.normalizeCode(payload['error']);
      }
    }

    if (status > 0) {
      return `HTTP_${status}`;
    }

    return 'UNKNOWN_ERROR';
  }

  private normalizeCode(value: string): string {
    const normalized = value
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();

    return normalized || 'UNKNOWN_ERROR';
  }

  private isMetaKey(key: string): boolean {
    return key === 'status' || key === 'statusCode' || key === 'timestamp' || key === 'path';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
